import type { TemplateSettings } from 'opensolar-common'
import { ViewportDeviceType } from 'opensolar-common'
import { createContext, Dispatch, useReducer } from 'react'
import type { ProposalV2Config } from 'types/proposalTemplate'

export const DISCARD_PROPOSAL_V2_TEMPLATE_SETTINGS = 'DISCARD_PROPOSAL_V2_TEMPLATE_SETTINGS'
export const RESET_DISCARD_PROPOSAL_V2_TEMPLATE_SETTINGS = 'RESET_DISCARD_PROPOSAL_V2_TEMPLATE_SETTINGS'
export const LOAD_PROPOSAL_V2_TEMPLATE_SETTINGS = 'LOAD_PROPOSAL_V2_TEMPLATE_SETTINGS'
export const REFRESH_PROPOSAL_V2_TEMPLATE_SETTINGS = 'REFRESH_PROPOSAL_V2_TEMPLATE_SETTINGS'
export const SET_IS_EDITOR_ACTIVE = 'SET_IS_EDITOR_ACTIVE'
export const SET_SUBMITTING_PROPOSAL_V2_TEMPLATE_SETTINGS = 'SET_SUBMITTING_PROPOSAL_V2_TEMPLATE_SETTINGS'
export const UNLOAD_PROPOSAL_V2_TEMPLATE_SETTINGS = 'UNLOAD_PROPOSAL_V2_TEMPLATE_SETTINGS'
export const UNSAVED_PROPOSAL_V2_TEMPLATE_SETTINGS_UPDATED = 'UNSAVED_PROPOSAL_V2_TEMPLATE_SETTINGS_UPDATED'
export const SET_VIEWPORT_DEVICE = 'SET_VIEWPORT_DEVICE'

export enum ProposalV2TemplateContextStatus {
  NOT_LOADED,
  LOADED,
}

type ProposalV2TemplateSettingsStateType =
  | {
      isEditorActive: boolean
      isDiscarding: boolean
      viewportDevice: ViewportDeviceType
      status: ProposalV2TemplateContextStatus.NOT_LOADED
      loadedTemplateSettings: undefined
      loadedProposalV2Config: undefined
      unsavedTemplateSettings: undefined
      proposalTemplateId: undefined
      submitting: false
    }
  | {
      isEditorActive: boolean
      isDiscarding: boolean
      viewportDevice: ViewportDeviceType
      status: ProposalV2TemplateContextStatus.LOADED
      loadedTemplateSettings: TemplateSettings
      loadedProposalV2Config: ProposalV2Config
      unsavedTemplateSettings: TemplateSettings | undefined
      proposalTemplateId: number
      submitting: boolean
    }

type Action =
  | {
      type: typeof LOAD_PROPOSAL_V2_TEMPLATE_SETTINGS
      payload: { templateSettings: TemplateSettings; proposalTemplateId: number; proposalV2Config: ProposalV2Config }
    }
  | { type: typeof UNLOAD_PROPOSAL_V2_TEMPLATE_SETTINGS }
  | {
      type: typeof REFRESH_PROPOSAL_V2_TEMPLATE_SETTINGS
      payload: { templateSettings: TemplateSettings; proposalTemplateId: number; proposalV2Config: ProposalV2Config }
    }
  | {
      type: typeof UNSAVED_PROPOSAL_V2_TEMPLATE_SETTINGS_UPDATED
      payload: { templateSettings: TemplateSettings | undefined }
    }
  | { type: typeof DISCARD_PROPOSAL_V2_TEMPLATE_SETTINGS }
  | { type: typeof RESET_DISCARD_PROPOSAL_V2_TEMPLATE_SETTINGS }
  | { type: typeof SET_IS_EDITOR_ACTIVE; payload: { value: boolean } }
  | { type: typeof SET_SUBMITTING_PROPOSAL_V2_TEMPLATE_SETTINGS; payload: { submitting: boolean } }
  | { type: typeof SET_VIEWPORT_DEVICE; payload: { viewportDevice: ViewportDeviceType } }

export const PROPOSAL_V2_TEMPLATE_SETTINGS_CONTEXT_INITIAL_STATE: ProposalV2TemplateSettingsStateType = {
  isEditorActive: false,
  isDiscarding: false,
  viewportDevice: ViewportDeviceType.current,
  loadedTemplateSettings: undefined,
  loadedProposalV2Config: undefined,
  proposalTemplateId: undefined,
  status: ProposalV2TemplateContextStatus.NOT_LOADED,
  submitting: false,
  unsavedTemplateSettings: undefined,
}

export function proposalV2TemplateSettingsReducer(
  state: ProposalV2TemplateSettingsStateType,
  action: Action
): ProposalV2TemplateSettingsStateType {
  switch (action.type) {
    case LOAD_PROPOSAL_V2_TEMPLATE_SETTINGS:
    case REFRESH_PROPOSAL_V2_TEMPLATE_SETTINGS:
      return {
        ...state,
        status: ProposalV2TemplateContextStatus.LOADED,
        loadedTemplateSettings: action.payload.templateSettings,
        loadedProposalV2Config: action.payload.proposalV2Config,
        proposalTemplateId: action.payload.proposalTemplateId,
      }
    case SET_VIEWPORT_DEVICE:
      return {
        ...state,
        viewportDevice: action.payload.viewportDevice,
      }
    case RESET_DISCARD_PROPOSAL_V2_TEMPLATE_SETTINGS:
      return {
        ...state,
        isDiscarding: false,
      }
    case DISCARD_PROPOSAL_V2_TEMPLATE_SETTINGS:
      return {
        ...state,
        isDiscarding: true,
        unsavedTemplateSettings: undefined,
      }
    case SET_SUBMITTING_PROPOSAL_V2_TEMPLATE_SETTINGS:
      if (state.status === ProposalV2TemplateContextStatus.NOT_LOADED) {
        return state
      }
      return {
        ...state,
        submitting: action.payload.submitting,
      }
    case UNLOAD_PROPOSAL_V2_TEMPLATE_SETTINGS:
      return {
        ...PROPOSAL_V2_TEMPLATE_SETTINGS_CONTEXT_INITIAL_STATE,
      }
    case UNSAVED_PROPOSAL_V2_TEMPLATE_SETTINGS_UPDATED:
      if (state.status === ProposalV2TemplateContextStatus.NOT_LOADED) {
        console.error('unsave proposal settings updated before it gets loaded!')
        return state
      }
      return {
        ...state,
        ...(JSON.stringify(state.loadedTemplateSettings) !== JSON.stringify(action.payload.templateSettings) && {
          unsavedTemplateSettings: action.payload.templateSettings,
        }),
      }
    case SET_IS_EDITOR_ACTIVE:
      return {
        ...state,
        isEditorActive: action.payload.value,
      }
    default:
      return state
  }
}

function uninitializedDispatcher(args: Action) {
  throw Error('Cannot dispatch action: ProposalV2TemplateSettingsContext is in NOT_LOADED state')
}

export const ProposalV2TemplateSettingsContext = createContext<{
  state: ProposalV2TemplateSettingsStateType
  dispatch: Dispatch<Action> | typeof uninitializedDispatcher
}>({ state: PROPOSAL_V2_TEMPLATE_SETTINGS_CONTEXT_INITIAL_STATE, dispatch: uninitializedDispatcher })

export const ProposalV2TemplateSettingsContextProvider = ({ children }) => {
  const [templateSettingsState, templateSettingsStateDispatch] = useReducer(
    proposalV2TemplateSettingsReducer,
    PROPOSAL_V2_TEMPLATE_SETTINGS_CONTEXT_INITIAL_STATE
  )

  return (
    <ProposalV2TemplateSettingsContext.Provider
      value={{ state: templateSettingsState, dispatch: templateSettingsStateDispatch }}
    >
      {children}
    </ProposalV2TemplateSettingsContext.Provider>
  )
}

export const proposalV2TemplateSettingsSelectors = {
  loadedTemplateSettings: (state: ProposalV2TemplateSettingsStateType): TemplateSettings | undefined =>
    state.loadedTemplateSettings,
  loadedProposalV2Config: (state: ProposalV2TemplateSettingsStateType): ProposalV2Config | undefined =>
    state.loadedProposalV2Config,
  proposalTemplateId: (state: ProposalV2TemplateSettingsStateType): number | undefined => state.proposalTemplateId,
  unsavedTemplateSettings: (state: ProposalV2TemplateSettingsStateType): TemplateSettings | undefined =>
    state.unsavedTemplateSettings,
  proposalV2TemplateContextStatus: (state: ProposalV2TemplateSettingsStateType): ProposalV2TemplateContextStatus =>
    state.status,
  isEditorActive: (state: ProposalV2TemplateSettingsStateType): boolean => state.isEditorActive,
  viewportDevice: (state: ProposalV2TemplateSettingsStateType): ViewportDeviceType => state.viewportDevice,
} as const
